import * as yup from 'yup'
import { useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Helmet } from 'react-helmet'

import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { useUpdateSurveyData } from '@/user/userQueries'
import { useEffect, useMemo, useCallback } from 'react'
import { APP } from '@/common/strings'
import { APP_ROUTES } from '@/common/constants'
import useUserStore from '@/user/userStore'
import { analytics } from '@/common/analytics/analytics'
import { ONBOARDING_COPY } from '@/locales/en'
import { isMobile } from '@/common/utils/utils'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { SurveyFormFields } from './SurveyFormFields'
import type { SurveyFormValues } from './surveyTypes'
import { useOnboardingProgress } from '@/onboarding/useOnboardingProgress'

export type SurveyT = SurveyFormValues

const schema = yup
    .object({
        occupation: yup.string().required('Please enter occupation').min(2, 'Please enter valid occupation'),
        investmentPurpose: yup.string().required('Please select an option'),
        expectedTradeFrequency: yup.string().required('Please select an option'),
        expectedTradeAmount: yup.string().required('Please select an option'),
    })
    .required()

export function Survey() {
    const { progress } = useOnboardingProgress()
    const { user, setUser } = useUserStore()
    const navigate = useNavigate()
    const updateSurveyData = useUpdateSurveyData()

    const {
        handleSubmit,
        setValue,
        trigger,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    })

    const onSubmit = (data: SurveyFormValues) => {
        updateSurveyData.mutate({
            occupation: data.occupation,
            investmentPurpose: data.investmentPurpose,
            expectedTradeFrequency: data.expectedTradeFrequency,
            expectedTradeAmount: data.expectedTradeAmount,
        })
        if (!user) return
        // Update user store with all survey data so it persists when navigating back
        setUser({
            ...user,
            occupation: data.occupation,
            survey: {
                investmentPurpose: data.investmentPurpose,
                expectedTradeFrequency: data.expectedTradeFrequency,
                expectedTradeAmount: data.expectedTradeAmount,
            },
        })
    }
    const onBack = () => {
        if (isMobile()) {
            navigate(APP_ROUTES.MOBILE_NUMBER)
            return
        }
        navigate(APP_ROUTES.ABOUT_YOU)
    }

    const prefillForm = useCallback(() => {
        setValue('occupation', user?.occupation || '')
        setValue('investmentPurpose', user?.survey?.investmentPurpose || '')
        setValue('expectedTradeFrequency', user?.survey?.expectedTradeFrequency || '')
        setValue('expectedTradeAmount', user?.survey?.expectedTradeAmount || '')
    }, [user, setValue])

    const trackAnalytics = useMemo(() => {
        return () => {
            analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.ONBOARDING_SURVEY_SUBMITTED, {
                occupation: user?.occupation || '',
                investmentPurpose: user?.survey?.investmentPurpose || '',
                expectedTradeFrequency: user?.survey?.expectedTradeFrequency || '',
                expectedTradeAmount: user?.survey?.expectedTradeAmount || '',
            })
            navigate(APP_ROUTES.ACCOUNT_TYPE)
        }
    }, [navigate, user])

    useEffect(() => {
        if (updateSurveyData.isSuccess) {
            trackAnalytics()
        }
    }, [updateSurveyData.isSuccess, trackAnalytics])

    useEffect(() => {
        prefillForm()
    }, [user, setValue, prefillForm])

    return (
        <>
            <Helmet>
                <title>
                    {ONBOARDING_COPY.survey.pageTitle} | {APP.title}
                </title>
            </Helmet>
            <Onboarding
                isLoading={updateSurveyData.isPending}
                title={ONBOARDING_COPY.survey.title}
                description={ONBOARDING_COPY.survey.description}
                cta={ONBOARDING_COPY.next}
                onSubmit={handleSubmit(onSubmit)}
                onBack={onBack}
                progress={progress}
            >
                <SurveyFormFields
                    labels={ONBOARDING_COPY.survey.form}
                    control={control}
                    setValue={setValue}
                    trigger={trigger}
                    errors={errors}
                    defaultValues={{
                        occupation: user?.occupation,
                        investmentPurpose: user?.survey?.investmentPurpose,
                        expectedTradeFrequency: user?.survey?.expectedTradeFrequency,
                        expectedTradeAmount: user?.survey?.expectedTradeAmount,
                    }}
                />
            </Onboarding>
        </>
    )
}
